// Story 10.5 — Capabilities y permisos. La UI chequea contra capabilities,
// NO contra roles directamente; los roles agregan capabilities. Más
// extensible que `if (role === 'admin')` regado.

// Story 16.1 — rol de PLATAFORMA. `member` (cuenta de cliente) reemplaza a
// `owner`; lo que un member puede hacer sobre cada bot sale de su rol en la
// organización del bot, y desde 16.2 lo calcula el servidor.
export type UserRole = 'admin' | 'support' | 'member';

/** Rol dentro de una organización. */
export type OrganizationRole = 'owner' | 'editor' | 'viewer';

export type Capability =
  | 'view_dashboard'
  | 'view_bot'
  | 'edit_bot'
  | 'deploy_bot'
  | 'view_conversations'
  | 'view_analytics_basic'
  | 'view_analytics_advanced'
  | 'view_logs'
  | 'manage_users'
  | 'view_settings'
  | 'manage_members'
  | 'manage_credentials';

export const ALL_CAPABILITIES: Capability[] = [
  'view_dashboard',
  'view_bot',
  'edit_bot',
  'deploy_bot',
  'view_conversations',
  'view_analytics_basic',
  'view_analytics_advanced',
  'view_logs',
  'manage_users',
  'view_settings',
  'manage_members',
  'manage_credentials',
];

/**
 * Mapa de roles de plataforma → capabilities. `'*'` = todas (admin).
 * Espejo de `apps/api/src/auth/capabilities.ts` (`member` = el antiguo
 * `owner` hasta 16.2).
 */
export const ROLE_CAPABILITIES: Record<UserRole, '*' | readonly Capability[]> = {
  admin: '*',
  member: [
    'view_dashboard',
    'view_bot',
    'view_conversations',
    'view_analytics_basic',
    'view_settings',
  ],
  support: [
    'view_dashboard',
    'view_bot',
    'view_conversations',
    'view_analytics_basic',
    'view_logs',
  ],
};

/**
 * Devuelve true si `role` puede ejecutar `capability`. Admin siempre matchea
 * (el `'*'` lo cubre); otros roles consultan su lista. Si el role es
 * desconocido, devuelve false (fail closed).
 */
export function hasPermission(role: UserRole | null | undefined, capability: Capability): boolean {
  if (!role) return false;
  const set = ROLE_CAPABILITIES[role];
  if (!set) return false;
  if (set === '*') return true;
  return set.includes(capability);
}

/** Type guard útil para validar payloads de auth/me. */
export function isUserRole(value: unknown): value is UserRole {
  return value === 'admin' || value === 'support' || value === 'member';
}
